/**
 * Maps template field keys to the column names used in the READINGS sheet, plus
 * display metadata for the trends/charts picker. Mirrors apps-script/Constants.gs TEMPLATE_V1.
 */
export interface FieldMeta {
  key: string;
  label: string;
  section: string;
  unit: string;
  col: string;
}

export const FIELD_META: FieldMeta[] = [
  { key: 'ht_voltage', label: 'HT Voltage', section: 'Incomer (HT) VCB', unit: 'V', col: 'HT Voltage' },
  { key: 'ht_amp', label: 'HT Amp', section: 'Incomer (HT) VCB', unit: 'A', col: 'HT Amp' },
  { key: 'ht_freq', label: 'HT Frequency', section: 'Incomer (HT) VCB', unit: 'Hz', col: 'HT Frequency' },
  { key: 'ht_amp_tr1', label: 'HT Amp TR-1', section: 'Incomer (HT) VCB', unit: 'A', col: 'HT Amp TR-1' },
  { key: 'ht_amp_tr2', label: 'HT Amp TR-2', section: 'Incomer (HT) VCB', unit: 'A', col: 'HT Amp TR-2' },
  { key: 'tr1_voltage', label: 'TR-1 Voltage', section: 'Transformer-1', unit: 'V', col: 'TR1 Voltage' },
  { key: 'tr1_amp', label: 'TR-1 Amp', section: 'Transformer-1', unit: 'A', col: 'TR1 Amp' },
  { key: 'tr1_freq', label: 'TR-1 Frequency', section: 'Transformer-1', unit: 'Hz', col: 'TR1 Frequency' },
  { key: 'tr1_temp_r', label: 'TR-1 Temp-R', section: 'Transformer-1', unit: '°C', col: 'TR1 Temp-R' },
  { key: 'tr1_temp_y', label: 'TR-1 Temp-Y', section: 'Transformer-1', unit: '°C', col: 'TR1 Temp-Y' },
  { key: 'tr1_temp_b', label: 'TR-1 Temp-B', section: 'Transformer-1', unit: '°C', col: 'TR1 Temp-B' },
  { key: 'tr1_pf', label: 'TR-1 PF', section: 'Transformer-1', unit: '', col: 'TR1 PF' },
  { key: 'tr2_voltage', label: 'TR-2 Voltage', section: 'Transformer-2', unit: 'V', col: 'TR2 Voltage' },
  { key: 'tr2_amp', label: 'TR-2 Amp', section: 'Transformer-2', unit: 'A', col: 'TR2 Amp' },
  { key: 'tr2_freq', label: 'TR-2 Frequency', section: 'Transformer-2', unit: 'Hz', col: 'TR2 Frequency' },
  { key: 'tr2_temp_r', label: 'TR-2 Temp-R', section: 'Transformer-2', unit: '°C', col: 'TR2 Temp-R' },
  { key: 'tr2_temp_y', label: 'TR-2 Temp-Y', section: 'Transformer-2', unit: '°C', col: 'TR2 Temp-Y' },
  { key: 'tr2_temp_b', label: 'TR-2 Temp-B', section: 'Transformer-2', unit: '°C', col: 'TR2 Temp-B' },
  { key: 'tr2_pf', label: 'TR-2 PF', section: 'Transformer-2', unit: '', col: 'TR2 PF' },
];

export const FIELD_BY_KEY: Record<string, FieldMeta> = Object.fromEntries(FIELD_META.map((f) => [f.key, f]));

export interface Point { t: number; v: number }

/**
 * Derive the READINGS sheet column for a field, matching apps-script/Tags.gs fieldColumn_.
 * Used as a fallback when a template field arrives without an explicit `col`.
 */
export function deriveColumn(fieldKey: string, sectionKey: string, label: string): string {
  const builtin = FIELD_BY_KEY[fieldKey];
  if (builtin) return builtin.col;
  const s = String(sectionKey || '').toUpperCase().replace(/[^A-Z0-9]+/g, '');
  return (s ? s + ' ' : '') + (label || fieldKey).trim();
}

/** Pull a numeric time series for one READINGS column out of admin reading rows. */
export function seriesFor(rows: Array<Record<string, unknown>>, col: string): Point[] {
  if (!col) return [];
  const out: Point[] = [];
  for (const r of rows) {
    const iso = String(r['Effective Reading At'] ?? r['Server Timestamp'] ?? '');
    const t = Date.parse(iso);
    const raw = r[col];
    const v = Number(raw);
    if (!Number.isNaN(t) && raw !== '' && raw != null && !Number.isNaN(v)) out.push({ t, v });
  }
  return out.sort((a, b) => a.t - b.t);
}

export function summarize(pts: Point[]): { min: number; max: number; avg: number; last: number } | null {
  if (!pts.length) return null;
  const vs = pts.map((p) => p.v);
  return {
    min: Math.min(...vs),
    max: Math.max(...vs),
    avg: vs.reduce((a, b) => a + b, 0) / vs.length,
    last: pts[pts.length - 1].v,
  };
}
